import type React from "react";

import { cn } from "src/shadcn/lib/utils";
import { HelpIcon, type HelpIconProps } from "src/shared/HelpIcon";
import {
  Field as ShadcnField,
  FieldLabel as ShadcnFieldLabel,
  FieldDescription as ShadcnFieldDescription,
  FieldError as ShadcnFieldError,
  FieldGroup as ShadcnFieldGroup,
  FieldLegend as ShadcnFieldLegend,
  FieldSeparator as ShadcnFieldSeparator,
  FieldSet as ShadcnFieldSet,
  FieldContent as ShadcnFieldContent,
  FieldTitle as ShadcnFieldTitle,
} from "src/shadcn/components/field";

// `relative` makes the field a positioning context. Radix form controls
// (Checkbox / RadioGroupItem / Switch) render a visually-hidden,
// `position: absolute` BubbleInput as a sibling of the control for native form
// submission. Without a positioned ancestor it resolves against the viewport
// and, for a control near the bottom of a scroll region, hangs a few px below
// the fold — adding a phantom document scrollbar. Containing it here keeps it
// clipped to the field. Applied in this primitive layer (not the CLI-generated
// shadcn component) so it survives shadcn CLI upgrades.
const Field = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnField>) => (
  <ShadcnField className={cn("relative", className)} {...props} />
);

type FieldLabelProps = React.ComponentProps<typeof ShadcnFieldLabel> & {
  /** Props for the help icon (tooltip or popover mode). */
  helpIconProps?: HelpIconProps;
};

const FieldLabel = ({ helpIconProps, children, ...props }: FieldLabelProps) => (
  <ShadcnFieldLabel {...props}>
    {children}
    {helpIconProps && <HelpIcon {...helpIconProps} />}
  </ShadcnFieldLabel>
);

const FieldDescription = (
  props: React.ComponentProps<typeof ShadcnFieldDescription>
) => <ShadcnFieldDescription {...props} />;

const FieldError = (props: React.ComponentProps<typeof ShadcnFieldError>) => (
  <ShadcnFieldError {...props} />
);

const FieldGroup = (props: React.ComponentProps<typeof ShadcnFieldGroup>) => (
  <ShadcnFieldGroup {...props} />
);

const FieldLegend = (props: React.ComponentProps<typeof ShadcnFieldLegend>) => (
  <ShadcnFieldLegend {...props} />
);

const FieldSeparator = (
  props: React.ComponentProps<typeof ShadcnFieldSeparator>
) => <ShadcnFieldSeparator {...props} />;

const FieldSet = (props: React.ComponentProps<typeof ShadcnFieldSet>) => (
  <ShadcnFieldSet {...props} />
);

const FieldContent = (
  props: React.ComponentProps<typeof ShadcnFieldContent>
) => <ShadcnFieldContent {...props} />;

const FieldTitle = (props: React.ComponentProps<typeof ShadcnFieldTitle>) => (
  <ShadcnFieldTitle {...props} />
);

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
};
